import { useId, useState, type ReactNode } from 'react'
import { Check, Copy, Eye, UserPlus } from 'lucide-react'

import type { PlatformRoleCode } from '@cattle/money'
import type {
  BroadcastRequest,
  BroadcastResponse,
  PatchPlatformSettingsRequest,
} from '@cattle/contracts'

import {
  Avatar,
  Banner,
  Button,
  ConfirmDialog,
  DangerZone,
  DataTable,
  Field,
  LoadingSkeleton,
  Modal,
  PageHeader,
  QueryBoundary,
  Section,
  Select,
  StatusBadge,
  TextInput,
  Toggle,
  useToast,
  type Column,
  type DangerRow,
} from '../../../components'
import { t } from '../../../lib/i18n'
import { useIsPlatformOwner, useSession } from '../../../app/session'
import { post, patch } from '../../../lib/apiClient'
import { useApiMutation } from '../../../lib/mutations'
import {
  queryKeys,
  useConsoleSettings,
  type ConsoleSettings,
  type Gateway,
  type TeamMember,
} from '../../../fixtures'

/**
 * P-11 — Console settings.
 *
 * THE RULE OF THIS SCREEN: every setting on it is the PLATFORM_OWNER's to
 * change. PLATFORM_SUPPORT gets the same screen, read only — same figures, same
 * order, same explanations of what each one costs, but no Save, no Add someone,
 * no control that can be moved, and no "Careful" section at all. The maintenance
 * switch and the broadcast composer are ABSENT for support rather than disabled:
 * a greyed-out "Turn on" still announces that a support account is one
 * permission away from locking every trader out.
 *
 * A single notice at the top says why the screen is quiet, so the reader is not
 * left wondering whether something failed to load.
 *
 * THREE THINGS THE WIRING HAD TO GET RIGHT
 *
 * 1. **Maintenance mode is real.** It is backed by `platform_settings` and makes
 *    every endpoint answer `403 MAINTENANCE_MODE`. The confirm therefore says
 *    plainly that nobody can sign in, and — because this is the switch that
 *    turns itself off — that the platform owner keeps this console.
 * 2. **The one-time password is shown once.** `POST /platform/team` returns it
 *    and nothing can read it back, so the reveal has a copy button and says so.
 * 3. **A broadcast is a receipt, not a delivery.** G22 is undecided: there is no
 *    provider, `deliver()` throws, and P19 persists nothing. The result modal
 *    says what actually happened — a count was accepted — and never that 47
 *    owners were messaged.
 */

/**
 * What "not kept forever" actually means, in the platform's own words.
 *
 * `keepOriginalVideoDays` is the field that says how long an original survives;
 * this option used to be labelled with `postTrialRetentionDays`, which is how
 * long a CANCELLED book's data is kept — a different setting entirely, and one
 * a reader would have taken for the video policy. When no age is set, the
 * policy is simply "do not keep them", and it says that rather than a number.
 */
function keepOriginalsLabel(data: ConsoleSettings): string {
  return data.keepOriginalDays === null
    ? t('p.settings.notKept')
    : `${data.keepOriginalDays} ${t('unit.days')}`
}

/** A setting rendered as words, for the reader who may not change it. */
function ReadValue({ children }: { children: ReactNode }) {
  return <span className="font-display text-[13.5px] font-bold tnum text-dark">{children}</span>
}

function OnOff({ on }: { on: boolean }) {
  return (
    <StatusBadge tone={on ? 'ok' : 'neutral'}>{on ? t('label.on') : t('label.off')}</StatusBadge>
  )
}

const MB = 1024 * 1024

/** A whole number typed into a box, or `undefined` when it is not one. */
function wholeNumber(value: string): number | undefined {
  const parsed = Number(value.trim())
  return Number.isFinite(parsed) && parsed >= 0 ? Math.round(parsed) : undefined
}

/** "02:00" — the hour is what is stored; the minutes are always zero. */
function hourOf(value: string): number | undefined {
  const hour = wholeNumber(value.split(':')[0] ?? '')
  return hour !== undefined && hour <= 23 ? hour : undefined
}

/**
 * CONTRACT GAP: `createTeamMemberResponse` exists as a schema but is not
 * exported as a TypeScript type, and `teamMemberSchema` — like `personSchema` —
 * has no `oneTimePassword` field. The password is read off the raw response
 * exactly as the server sends it; parsing against the schema would strip the
 * one field the reveal is for.
 */
interface CreatedTeamMember {
  member: { id: string; name: string }
  oneTimePassword?: string
}

const BROADCAST_CHANNELS = ['WHATSAPP', 'SMS', 'EMAIL', 'IN_APP'] as const
type BroadcastChannel = (typeof BROADCAST_CHANNELS)[number]

function channelLabel(channel: BroadcastChannel): string {
  switch (channel) {
    case 'WHATSAPP':
      return t('p.support.channelWHATSAPP')
    case 'SMS':
      return t('p.settings.channelSMS')
    case 'EMAIL':
      return t('p.support.channelEMAIL')
    case 'IN_APP':
      return t('p.support.channelIN_APP')
  }
}

export function ConsoleSettingsScreen() {
  const settings = useConsoleSettings()

  return (
    <QueryBoundary
      query={settings}
      skeleton={
        <div className="grid gap-3.5 lg:grid-cols-2">
          <LoadingSkeleton variant="panel" />
          <LoadingSkeleton variant="panel" />
          <LoadingSkeleton variant="panel" />
          <LoadingSkeleton variant="panel" />
        </div>
      }
    >
      {(data) => <SettingsBody data={data} />}
    </QueryBoundary>
  )
}

function SettingsBody({ data }: { data: ConsoleSettings }) {
  const isOwner = useIsPlatformOwner()
  const { platformUserName } = useSession()
  const toast = useToast()

  const [trialDays, setTrialDays] = useState(String(data.trialDays))
  const [askForMethod, setAskForMethod] = useState(data.askForMethod)
  const [keepData, setKeepData] = useState(data.keepDataAfterTrial)
  const [keepDataDays, setKeepDataDays] = useState(String(data.keepDataDays))
  const [retryAfterDays, setRetryAfterDays] = useState(String(data.retryAfterDays))
  const [largestVideoMb, setLargestVideoMb] = useState(String(data.largestVideoMb))
  const [keepOriginals, setKeepOriginals] = useState<ConsoleSettings['keepOriginals']>(
    data.keepOriginals,
  )
  const [backupAt, setBackupAt] = useState(data.backupAt)

  const [maintenanceOpen, setMaintenanceOpen] = useState(false)
  const [broadcastOpen, setBroadcastOpen] = useState(false)
  const [broadcastChannel, setBroadcastChannel] = useState<BroadcastChannel>('WHATSAPP')
  const [broadcastSubject, setBroadcastSubject] = useState('')
  const [broadcast, setBroadcast] = useState('')
  const [receipt, setReceipt] = useState<BroadcastResponse | null>(null)

  const [addOpen, setAddOpen] = useState(false)
  const [memberName, setMemberName] = useState('')
  const [memberUsername, setMemberUsername] = useState('')
  const [memberRole, setMemberRole] = useState<PlatformRoleCode>('PLATFORM_SUPPORT')
  const [added, setAdded] = useState<CreatedTeamMember | null>(null)

  const subjectId = useId()
  const channelId = useId()
  const memberNameId = useId()
  const memberUsernameId = useId()
  const memberRoleId = useId()

  /*
    ONE PATCH, and only the fields that actually moved: `patchPlatformSettings`
    refuses an empty body, and sending every field back writes an audit row for
    each. Maintenance mode is deliberately NOT in here — it has its own confirm,
    and a switch that locks out every trader must never ride along with a save.
  */
  const settingsBody = (): PatchPlatformSettingsRequest => {
    const body: PatchPlatformSettingsRequest = {}

    const trial = wholeNumber(trialDays)
    if (trial !== undefined && trial !== data.trialDays) body.trialDays = trial

    if (askForMethod !== data.askForMethod) body.requirePaymentMethod = askForMethod

    // The toggle and the box are one field on the wire: zero days IS "do not
    // keep it". Sending them separately would let the two disagree.
    const retention = keepData ? wholeNumber(keepDataDays) : 0
    if (retention !== undefined && retention !== data.keepDataDays) {
      body.postTrialRetentionDays = retention
    }

    const retry = wholeNumber(retryAfterDays)
    if (retry !== undefined && retry !== data.retryAfterDays) body.dunningRetryAfterDays = retry

    const videoMb = wholeNumber(largestVideoMb)
    if (videoMb !== undefined && videoMb !== data.largestVideoMb) body.maxVideoBytes = videoMb * MB

    if (keepOriginals !== data.keepOriginals) {
      body.keepOriginalVideos = keepOriginals === 'FOREVER'
    }

    const hour = hourOf(backupAt)
    if (hour !== undefined && `${String(hour).padStart(2, '0')}:00` !== data.backupAt) {
      body.nightlyBackupHourPkt = hour
    }

    return body
  }

  const save = useApiMutation<void, unknown>({
    run: async () => {
      const body = settingsBody()
      if (Object.keys(body).length === 0) return null
      return patch('/platform/settings', body)
    },
    // The video ceiling and the originals lever both move screen 07's figures,
    // and the trial length moves the overview's counts.
    invalidates: [queryKeys.consoleSettings, queryKeys.usage, queryKeys.platformOverview],
    success: (result) =>
      result === null ? t('p.settings.nothingChanged') : t('p.settings.saved'),
  })

  /*
    Maintenance mode, on its own mutation and its own confirm. `data.maintenance`
    is what the server says the flag is, so the button offers the move that is
    actually available rather than always offering "Turn on".
  */
  const maintenance = useApiMutation<boolean, unknown>({
    run: async (next) => patch('/platform/settings', { maintenanceMode: next }),
    invalidates: [queryKeys.consoleSettings, queryKeys.platformOverview],
    success: (_result, next) =>
      next ? t('p.settings.maintenanceOn') : t('p.settings.maintenanceOff'),
  })

  const addMember = useApiMutation<void, CreatedTeamMember>({
    /*
      The response is taken RAW. `teamMemberSchema` has no `oneTimePassword`
      field, so anything that parsed this against the contract would drop the
      only part of it that cannot be fetched again.
    */
    run: async () =>
      post<CreatedTeamMember>('/platform/team', {
        name: memberName.trim(),
        username: memberUsername.trim(),
        role: memberRole,
      }),
    invalidates: [queryKeys.consoleSettings],
    success: (result) => t('p.settings.memberAdded', { name: result.member.name }),
    onDone: (result) => {
      setAddOpen(false)
      setMemberName('')
      setMemberUsername('')
      setMemberRole('PLATFORM_SUPPORT')
      // Shown ONCE. Nothing reads it back, so it is held in state only until
      // the reveal is closed.
      setAdded(result)
    },
  })

  const sendBroadcast = useApiMutation<void, BroadcastResponse>({
    run: async () => {
      const body: BroadcastRequest = {
        channel: broadcastChannel,
        subject: broadcastSubject.trim(),
        body: broadcast.trim(),
        audience: {},
      }
      return post<BroadcastResponse>('/platform/broadcast', body)
    },
    // Nothing is stored and nothing is delivered, so nothing cached has moved.
    onDone: (result) => {
      setBroadcastOpen(false)
      setBroadcast('')
      setBroadcastSubject('')
      setReceipt(result)
    },
  })

  const copyPassword = (password: string) => {
    void navigator.clipboard.writeText(password).then(
      () => toast.show(t('act.copied')),
      () => toast.show(t('p.settings.memberPasswordBody'), 'danger'),
    )
  }

  const teamColumns: Column<TeamMember>[] = [
    {
      id: 'name',
      header: t('label.name'),
      cell: (member) => (
        <div className="flex items-center gap-2.5">
          <Avatar name={member.name} size="sm" />
          <div className="min-w-0">
            <div className="font-semibold text-dark">{member.name}</div>
            <div className="text-[11.5px] text-muted">
              {member.name === platformUserName
                ? t('p.settings.you')
                : member.role === 'PLATFORM_SUPPORT'
                  ? t('p.settings.support')
                  : t('platformRole.PLATFORM_OWNER')}
            </div>
          </div>
        </div>
      ),
    },
    {
      id: 'canDo',
      header: t('p.settings.colCanDo'),
      cell: (member) =>
        member.role === 'PLATFORM_OWNER' ? (
          <StatusBadge tone="ok">{t('p.settings.everything')}</StatusBadge>
        ) : (
          <StatusBadge tone="warn">{t(`platformRole.${member.role}`)}</StatusBadge>
        ),
    },
    {
      id: 'booksOpened',
      header: t('p.settings.colBooksOpened'),
      numeric: true,
      sortBy: (member) => member.booksOpened,
      cell: (member) => (
        <span className="font-display font-bold tnum">{member.booksOpened}</span>
      ),
    },
  ]

  const dangerRows: DangerRow[] = [
    {
      id: 'maintenance',
      label: t('p.settings.maintenance'),
      description: data.maintenance
        ? t('p.settings.maintenanceIsOn')
        : t('p.settings.maintenanceBody'),
      action: (
        <Button
          variant="danger"
          disabled={maintenance.isPending}
          onClick={() => setMaintenanceOpen(true)}
        >
          {data.maintenance ? t('p.settings.turnOff') : t('p.settings.turnOn')}
        </Button>
      ),
    },
    {
      id: 'broadcast',
      label: t('p.settings.broadcast'),
      description: t('p.settings.broadcastBody', { count: data.ownerCount }),
      action: <Button onClick={() => setBroadcastOpen(true)}>{t('p.settings.compose')}</Button>,
    },
  ]

  const memberReady = memberName.trim().length >= 2 && memberUsername.trim().length >= 3
  const broadcastReady = broadcastSubject.trim().length >= 3 && broadcast.trim().length >= 3

  return (
    <>
      <PageHeader
        title={t('p.settings.title')}
        subtitle={t('p.settings.subtitle', { count: data.ownerCount })}
        actions={
          isOwner ? (
            <Button
              variant="primary"
              icon={<Check className="size-4" />}
              disabled={save.isPending}
              onClick={() => save.mutate()}
            >
              {t('act.saveChanges')}
            </Button>
          ) : undefined
        }
      />

      {/* Said once, near the top: nothing here is broken, it is simply not yours. */}
      {isOwner ? null : (
        <Banner
          tone="accent"
          icon={<Eye className="size-5" />}
          title={t('p.settings.readOnlyNotice')}
        />
      )}

      {/* The flag the whole product hangs off, stated wherever it is true. */}
      {data.maintenance ? (
        <Banner tone="danger" title={t('p.settings.maintenanceIsOn')} />
      ) : null}

      <div className="grid items-start gap-3.5 lg:grid-cols-2">
        <div className="flex flex-col gap-3.5">
          <Section
            title={t('p.settings.team')}
            action={
              isOwner ? (
                <Button size="sm" onClick={() => setAddOpen(true)}>
                  {t('p.settings.addSomeone')}
                </Button>
              ) : undefined
            }
          >
            <DataTable
              columns={teamColumns}
              rows={data.team}
              rowKey={(member) => member.id}
              caption={t('p.settings.team')}
            />
            <p className="mt-3.5 border-t border-muted/15 pt-3.5 text-xs leading-relaxed text-muted">
              {t('p.settings.teamNote')}
            </p>
          </Section>

          <Section title={t('p.settings.trials')}>
            <Field layout="row" label={t('p.settings.trialLength')} htmlFor="trial-days">
              {isOwner ? (
                <div className="flex items-center gap-2">
                  <TextInput
                    id="trial-days"
                    value={trialDays}
                    onChange={setTrialDays}
                    className="max-w-[92px] font-display tnum"
                    ariaLabel={t('p.settings.trialLength')}
                  />
                  <span className="font-display text-xs font-bold text-muted">
                    {t('unit.days')}
                  </span>
                </div>
              ) : (
                <ReadValue>
                  {data.trialDays} {t('unit.days')}
                </ReadValue>
              )}
            </Field>

            <Field
              layout="row"
              label={t('p.settings.askForMethod')}
              hint={t('p.settings.askForMethodHint')}
            >
              {isOwner ? (
                <Toggle
                  checked={askForMethod}
                  onChange={setAskForMethod}
                  label={<span className="sr-only">{t('p.settings.askForMethod')}</span>}
                />
              ) : (
                <OnOff on={data.askForMethod} />
              )}
            </Field>

            <Field
              layout="row"
              label={t('p.settings.keepData')}
              hint={t('p.settings.keepDataHint')}
              htmlFor="keep-data-days"
            >
              {isOwner ? (
                <div className="flex flex-wrap items-center gap-3">
                  <Toggle
                    checked={keepData}
                    onChange={setKeepData}
                    label={<span className="sr-only">{t('p.settings.keepData')}</span>}
                    className="w-auto"
                  />
                  <div className="flex items-center gap-2">
                    <TextInput
                      id="keep-data-days"
                      value={keepDataDays}
                      onChange={setKeepDataDays}
                      className="max-w-[92px] font-display tnum"
                      ariaLabel={t('p.settings.keepData')}
                    />
                    <span className="font-display text-xs font-bold text-muted">
                      {t('unit.days')}
                    </span>
                  </div>
                </div>
              ) : (
                <div className="flex items-center gap-2.5">
                  <OnOff on={data.keepDataAfterTrial} />
                  <ReadValue>
                    {data.keepDataDays} {t('unit.days')}
                  </ReadValue>
                </div>
              )}
            </Field>
          </Section>
        </div>

        <div className="flex flex-col gap-3.5">
          <Section title={t('p.settings.howYouGetPaid')}>
            {data.gateways.map((gateway, index) => (
              <GatewayRow key={gateway.method} gateway={gateway} first={index === 0} />
            ))}

            <Field layout="row" label={t('p.settings.retryAfter')} htmlFor="retry-days">
              {isOwner ? (
                <div className="flex items-center gap-2">
                  <TextInput
                    id="retry-days"
                    value={retryAfterDays}
                    onChange={setRetryAfterDays}
                    className="max-w-[92px] font-display tnum"
                    ariaLabel={t('p.settings.retryAfter')}
                  />
                  <span className="font-display text-xs font-bold text-muted">
                    {t('unit.days')}
                  </span>
                </div>
              ) : (
                <ReadValue>
                  {data.retryAfterDays} {t('unit.days')}
                </ReadValue>
              )}
            </Field>
            <p className="mt-3.5 border-t border-muted/15 pt-3.5 text-xs leading-relaxed text-muted">
              {t('p.settings.retryNote', { count: data.retryAfterDays })}
            </p>
          </Section>

          <Section title={t('p.settings.limits')}>
            <Field
              layout="row"
              label={t('p.settings.largestVideo')}
              hint={t('p.settings.largestVideoHint')}
              htmlFor="largest-video"
            >
              {isOwner ? (
                <div className="flex items-center gap-2">
                  <TextInput
                    id="largest-video"
                    value={largestVideoMb}
                    onChange={setLargestVideoMb}
                    className="max-w-[92px] font-display tnum"
                    ariaLabel={t('p.settings.largestVideo')}
                  />
                  <span className="font-display text-xs font-bold text-muted">
                    {t('unit.mbShort')}
                  </span>
                </div>
              ) : (
                <ReadValue>{t('unit.mb', { value: data.largestVideoMb })}</ReadValue>
              )}
            </Field>

            <Field
              layout="row"
              label={t('p.settings.keepOriginals')}
              hint={t('p.settings.keepOriginalsHint')}
              htmlFor="keep-originals"
            >
              {isOwner ? (
                <Select<ConsoleSettings['keepOriginals']>
                  id="keep-originals"
                  value={keepOriginals}
                  onChange={setKeepOriginals}
                  ariaLabel={t('p.settings.keepOriginals')}
                  options={[
                    { value: 'FOREVER', label: t('p.settings.forever') },
                    {
                      value: 'DROPPED',
                      label: keepOriginalsLabel(data),
                    },
                  ]}
                  className="max-w-[170px]"
                />
              ) : (
                <ReadValue>
                  {data.keepOriginals === 'FOREVER'
                    ? t('p.settings.forever')
                    : keepOriginalsLabel(data)}
                </ReadValue>
              )}
            </Field>

            <Field layout="row" label={t('p.settings.backupAt')} htmlFor="backup-at">
              {isOwner ? (
                <TextInput
                  id="backup-at"
                  value={backupAt}
                  onChange={setBackupAt}
                  className="max-w-[110px] font-display tnum"
                  ariaLabel={t('p.settings.backupAt')}
                />
              ) : (
                <ReadValue>{data.backupAt}</ReadValue>
              )}
            </Field>
          </Section>

          {/*
            Owner only, and ABSENT rather than disabled for support. A disabled
            "Turn on" is still a disclosure that one account can lock out 47
            trading businesses.
          */}
          {isOwner ? <DangerZone body={t('p.settings.dangerBody')} rows={dangerRows} /> : null}
        </div>
      </div>

      {isOwner ? (
        <>
          <ConfirmDialog
            open={maintenanceOpen}
            onClose={() => setMaintenanceOpen(false)}
            onConfirm={() => maintenance.mutate(!data.maintenance)}
            tone="danger"
            title={
              data.maintenance
                ? t('p.settings.maintenanceOffConfirmTitle')
                : t('p.settings.maintenanceConfirmTitle')
            }
            body={
              data.maintenance
                ? t('p.settings.maintenanceOffConfirmBody')
                : t('p.settings.maintenanceConfirmBody')
            }
            /* Stated on the way in: it is the reason this is reversible. */
            consequence={data.maintenance ? undefined : t('p.settings.maintenanceExempt')}
            disabled={maintenance.isPending}
            confirmLabel={data.maintenance ? t('p.settings.turnOff') : t('p.settings.turnOn')}
          />

          <Modal
            open={addOpen}
            onClose={() => setAddOpen(false)}
            title={t('p.settings.addSomeoneTitle')}
            subtitle={t('p.settings.addSomeoneSubtitle')}
            footer={
              <>
                <Button variant="ghost" onClick={() => setAddOpen(false)}>
                  {t('common.cancel')}
                </Button>
                <Button
                  variant="primary"
                  icon={<UserPlus className="size-4" />}
                  disabled={!memberReady || addMember.isPending}
                  onClick={() => addMember.mutate()}
                >
                  {memberReady
                    ? t('p.settings.addSomeoneSubmit', { name: memberName.trim() })
                    : t('p.settings.addSomeone')}
                </Button>
              </>
            }
          >
            <div className="grid gap-x-3.5 md:grid-cols-2">
              <Field required label={t('label.name')} htmlFor={memberNameId}>
                <TextInput
                  id={memberNameId}
                  value={memberName}
                  onChange={setMemberName}
                  className="max-w-none"
                />
              </Field>
              <Field required label={t('label.username')} htmlFor={memberUsernameId}>
                <TextInput
                  id={memberUsernameId}
                  value={memberUsername}
                  onChange={setMemberUsername}
                  className="max-w-none"
                />
              </Field>
            </div>
            <Field
              label={t('label.role')}
              hint={t('p.settings.teamRoleHint')}
              htmlFor={memberRoleId}
            >
              <Select<PlatformRoleCode>
                id={memberRoleId}
                value={memberRole}
                onChange={setMemberRole}
                options={[
                  { value: 'PLATFORM_SUPPORT', label: t('platformRole.PLATFORM_SUPPORT') },
                  { value: 'PLATFORM_OWNER', label: t('platformRole.PLATFORM_OWNER') },
                ]}
              />
            </Field>
          </Modal>

          {/*
            Shown once, and only because the server just said it. There is no
            route that returns it again, so this modal is the whole of the
            reader's opportunity — hence the copy button and the warning.
          */}
          <Modal
            open={added !== null}
            onClose={() => setAdded(null)}
            title={t('p.settings.memberPasswordTitle', { name: added?.member.name ?? '' })}
            footer={
              <Button variant="primary" onClick={() => setAdded(null)}>
                {t('act.close')}
              </Button>
            }
          >
            {added?.oneTimePassword ? (
              <>
                <div className="flex flex-wrap items-center gap-2.5">
                  <span className="flex h-11 items-center rounded-[10px] border border-glass-border bg-glass px-4 font-display text-lg font-bold tracking-[0.10em] tnum text-dark">
                    {added.oneTimePassword}
                  </span>
                  <Button
                    icon={<Copy className="size-3.5" />}
                    onClick={() => copyPassword(added.oneTimePassword ?? '')}
                  >
                    {t('act.copy')}
                  </Button>
                </div>
                <p className="mt-3 rounded-r border border-warn/30 bg-warn/10 p-3 text-[12.5px] leading-relaxed text-warn">
                  {t('p.settings.memberPasswordBody')}
                </p>
              </>
            ) : (
              <p className="text-[13.5px] leading-relaxed text-sub">
                {t('p.settings.memberPasswordSetByYou')}
              </p>
            )}
          </Modal>

          <Modal
            open={broadcastOpen}
            onClose={() => setBroadcastOpen(false)}
            title={t('p.settings.broadcastTitle')}
            subtitle={t('p.settings.broadcastBody', { count: data.ownerCount })}
            footer={
              <>
                <Button variant="ghost" onClick={() => setBroadcastOpen(false)}>
                  {t('common.cancel')}
                </Button>
                <Button
                  variant="primary"
                  disabled={!broadcastReady || sendBroadcast.isPending}
                  onClick={() => sendBroadcast.mutate()}
                >
                  {t('p.settings.broadcastSend', { count: data.ownerCount })}
                </Button>
              </>
            }
          >
            <div className="grid gap-x-3.5 md:grid-cols-2">
              <Field required label={t('p.settings.broadcastSubject')} htmlFor={subjectId}>
                <TextInput
                  id={subjectId}
                  value={broadcastSubject}
                  onChange={setBroadcastSubject}
                  className="max-w-none"
                />
              </Field>
              <Field label={t('p.settings.broadcastChannel')} htmlFor={channelId}>
                <Select<BroadcastChannel>
                  id={channelId}
                  value={broadcastChannel}
                  onChange={setBroadcastChannel}
                  options={BROADCAST_CHANNELS.map((channel) => ({
                    value: channel,
                    label: channelLabel(channel),
                  }))}
                />
              </Field>
            </div>

            <Field label={t('p.settings.broadcastLabel')} htmlFor="broadcast-text">
              <textarea
                id="broadcast-text"
                rows={4}
                value={broadcast}
                onChange={(event) => setBroadcast(event.target.value)}
                placeholder={t('p.settings.broadcastPlaceholder')}
                className="w-full rounded-[10px] border border-glass-border bg-glass px-3.5 py-3 text-[13.5px] leading-relaxed font-semibold text-dark outline-none placeholder:font-medium placeholder:text-light"
              />
            </Field>

            {/* The recipient sees this, not the composer's draft box. */}
            <p className="mt-1 text-xs font-semibold text-muted">
              {t('p.settings.broadcastPreview', { count: data.ownerCount })}
            </p>
            <div className="mt-2 rounded-r border border-accent-glass-border bg-accent-glass p-3.5">
              <p className="text-[13px] leading-relaxed whitespace-pre-wrap text-dark">
                {broadcast.trim() ? broadcast : t('p.settings.broadcastPlaceholder')}
              </p>
            </div>

            {/* Said before the button is pressed, not only after. */}
            <p className="mt-3 rounded-r border border-warn/30 bg-warn/10 p-3 text-[12.5px] leading-relaxed text-warn">
              {t('p.settings.broadcastNote')}
            </p>
          </Modal>

          {/*
            The receipt. It reports the number the server ACCEPTED and its
            reference, and says in the same breath that nothing was delivered
            and nothing was stored — which is the truth of P19 today.
          */}
          <Modal
            open={receipt !== null}
            onClose={() => setReceipt(null)}
            title={t('p.settings.broadcastReceiptTitle')}
            footer={
              <Button variant="primary" onClick={() => setReceipt(null)}>
                {t('act.close')}
              </Button>
            }
          >
            <p className="text-[13.5px] leading-relaxed text-sub">
              {t('p.settings.broadcastQueued', {
                count: receipt?.queued ?? 0,
                id: receipt?.broadcastId ?? '',
              })}
            </p>
            <p className="mt-3 rounded-r border border-warn/30 bg-warn/10 p-3 text-[12.5px] leading-relaxed text-warn">
              {t('p.settings.broadcastNothingSent')}
            </p>
          </Modal>
        </>
      ) : null}
    </>
  )
}

/**
 * One payment route, with what it actually costs to run beside it: Bank
 * transfer is Live in the same sense as the others, but every one of its five
 * businesses is a payment somebody confirms by hand.
 */
function GatewayRow({ gateway, first }: { gateway: Gateway; first: boolean }) {
  const usage =
    gateway.mode === 'MANUAL'
      ? t('p.settings.manualNote', { count: gateway.businesses })
      : first
        ? t('p.settings.usedBy', { count: gateway.businesses })
        : t('p.settings.usedByShort', { count: gateway.businesses })

  return (
    <div className="flex items-center justify-between gap-3.5 border-b border-muted/15 py-[15px]">
      <div className="min-w-0">
        <div className="text-[13.5px] font-semibold text-dark">
          {t(`paymentMethod.${gateway.method}`)}
        </div>
        <small className="mt-0.5 block text-[11.5px] leading-snug text-muted">{usage}</small>
      </div>
      <StatusBadge tone={gateway.mode === 'LIVE' ? 'ok' : 'neutral'}>
        {gateway.mode === 'LIVE' ? t('p.settings.live') : t('p.settings.manual')}
      </StatusBadge>
    </div>
  )
}
